import * as React from "react";
import {Grid2 as Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {CardEx} from "@/lib/components/CardEx";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import DateInputField from "@/lib/components/inputfields/date-input-field";
import {InvitationInfoDTO} from "@/api/data-contracts";
import {InvitationI18N} from "@/translations";
import {formatInvitationDate, formatValidUntil} from "@/model/invitation";

/**
 * Props for the InvitationInfoCard component.
 *
 * @property {InvitationInfoDTO} [invitation] - The read-only invitation info to display.
 */
interface InvitationInfoCardProps {
    invitation?: InvitationInfoDTO;
}

/**
 * Read-only card showing the invitation details of a company or membership.
 *
 * Renders "Eingeladen am", "Letzte Einladung am" and "Gültig bis". Missing token-derived
 * fields fall back to a placeholder; an expired validity shows the "abgelaufen" hint.
 *
 * @param {InvitationInfoCardProps} props - The component props.
 * @returns {React.ReactElement} The rendered card.
 */
export const InvitationInfoCard: React.FC<InvitationInfoCardProps> = ({invitation}: InvitationInfoCardProps): React.ReactElement => {
    const {t} = useTranslation();

    return (
        <CardEx title={t(InvitationI18N.invitationCardTitle)}>
            <Grid container spacing={2} width="100%">
                <Grid size={{xs: 6}}>
                    <DateInputField
                        name="invitation.invitedAt"
                        disabled
                        value={invitation?.invitedAt}
                        label={t(InvitationI18N.invitedAtLabel)}
                    />
                </Grid>
                <Grid size={{xs: 6}}>
                    <StringInputField
                        value={formatInvitationDate(invitation?.lastInvitationAt)}
                        label={t(InvitationI18N.lastInvitationAtLabel)}
                        disabled
                    />
                </Grid>
                <Grid size={{xs: 6}}>
                    <StringInputField
                        value={formatValidUntil(
                            invitation?.validUntil,
                            invitation?.expired,
                            t(InvitationI18N.expiredHint)
                        )}
                        label={t(InvitationI18N.validUntilLabel)}
                        disabled
                    />
                </Grid>
            </Grid>
        </CardEx>
    );
};

export default InvitationInfoCard;
